import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Lock, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AuthShell, AuthCard, authInputClass } from '../components/Common/AuthShell';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { t } = useTranslation('auth');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (!token) {
      toast.error(t('resetPassword.invalidLink'));
      navigate('/login');
      return;
    }

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async () => {
    setIsVerifying(true);

    try {
      const response = await fetch(`${API_URL}/auth/verify-reset-token?token=${token}`);
      const data = await response.json();

      if (!response.ok || !data.valid) {
        toast.error(data.error || t('resetPassword.invalidOrExpired'));
        setTimeout(() => navigate('/forgot-password'), 2000);
        return;
      }

      setTokenValid(true);
      setUserEmail(data.email);
    } catch (error: any) {
      console.error('Verify token error:', error);
      toast.error(t('resetPassword.verifyFailed'));
      setTimeout(() => navigate('/forgot-password'), 2000);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error(t('common.fillAllFields'));
      return;
    }

    if (newPassword.length < 8) {
      toast.error(t('resetPassword.passwordTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t('common.passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('resetPassword.resetFailed'));
      }

      setResetSuccess(true);
      toast.success(t('resetPassword.successToast'));
      setTimeout(() => navigate('/login'), 3000);
    } catch (error: any) {
      console.error('Reset password error:', error);
      toast.error(error.message || t('resetPassword.resetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <AuthShell hideFooter>
        <div className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-cyan-300 border-t-transparent" />
          <p className="text-white/[0.6]">{t('resetPassword.verifying')}</p>
        </div>
      </AuthShell>
    );
  }

  if (resetSuccess) {
    return (
      <AuthShell hideFooter>
        <AuthCard className="text-center">
          <div className="mb-6 inline-flex">
            <div className="grid h-20 w-20 place-items-center rounded-full bg-gradient-to-br from-lime-300 to-emerald-400 shadow-glow">
              <CheckCircle className="h-11 w-11 text-growth-ink" />
            </div>
          </div>
          <h1 className="mb-4 text-3xl font-black">{t('resetPassword.successTitle')}</h1>
          <p className="mb-8 text-white/[0.6]">{t('resetPassword.successDescription')}</p>
          <button type="button" onClick={() => navigate('/login')} className="landing-primary-cta w-full">
            {t('resetPassword.goToLogin')}
          </button>
          <p className="mt-4 text-sm text-white/40">{t('resetPassword.redirecting')}</p>
        </AuthCard>
      </AuthShell>
    );
  }

  if (!tokenValid) {
    return null;
  }

  return (
    <AuthShell tagline={t('resetPassword.tagline')} back={{ label: t('common.backToLogin'), to: '/login' }}>
      <AuthCard>
        <div className="mb-6">
          <h2 className="mb-2 text-2xl font-black">{t('resetPassword.title')}</h2>
          <p className="text-sm text-white/[0.6]">
            {t('resetPassword.creatingFor')} <strong className="text-white">{userEmail}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Password Input */}
          <div>
            <label htmlFor="newPassword" className="mb-2 block text-sm font-semibold text-white/80">
              {t('common.newPassword')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <Lock className="h-5 w-5 text-white/40" />
              </div>
              <input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`${authInputClass} pr-11`}
                placeholder={t('common.passwordPlaceholder')}
                disabled={isLoading}
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-white/40 transition hover:text-white/70"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-white/40">{t('resetPassword.passwordHint')}</p>
          </div>

          {/* Confirm Password Input */}
          <div>
            <label htmlFor="confirmPassword" className="mb-2 block text-sm font-semibold text-white/80">
              {t('common.confirmNewPassword')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <Lock className="h-5 w-5 text-white/40" />
              </div>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`${authInputClass} pr-11`}
                placeholder={t('common.passwordPlaceholder')}
                disabled={isLoading}
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-white/40 transition hover:text-white/70"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Password Match Indicator */}
          {newPassword && confirmPassword && (
            <div className={`text-sm font-semibold ${newPassword === confirmPassword ? 'text-lime-300' : 'text-rose-400'}`}>
              {newPassword === confirmPassword ? t('resetPassword.matched') : t('resetPassword.notMatched')}
            </div>
          )}

          {/* Submit Button */}
          <button type="submit" disabled={isLoading} className="landing-primary-cta w-full disabled:cursor-not-allowed disabled:opacity-60">
            {isLoading ? (
              <>
                <svg className="-ml-1 mr-1 h-5 w-5 animate-spin text-growth-ink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('resetPassword.submitting')}
              </>
            ) : (
              <>
                <Lock className="h-5 w-5" />
                {t('resetPassword.submit')}
              </>
            )}
          </button>
        </form>
      </AuthCard>
    </AuthShell>
  );
};
